import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import { ErrorOutline, Refresh } from '@mui/icons-material';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  info: ErrorInfo | null;
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Pick<ErrorBoundaryState, 'error'> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] Uncaught render error:', error);
    console.error('[ErrorBoundary] Component stack:', info.componentStack);
    this.setState({ info });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (!this.state.error) return this.props.children;

    const { error, info } = this.state;

    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          bgcolor: 'background.default',
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 720, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <ErrorOutline sx={{ fontSize: 48, color: 'error.main' }} />
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Algo salió mal
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ocurrió un error inesperado al renderizar esta parte de la aplicación.
              </Typography>
            </Box>
          </Box>

          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {error.name || 'Error'}: {error.message || 'Error sin mensaje'}
            </Typography>
          </Alert>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button variant="contained" startIcon={<Refresh />} onClick={this.handleReload}>
              Recargar página
            </Button>
            <Button variant="outlined" onClick={this.handleGoHome}>
              Ir al dashboard
            </Button>
          </Box>

          <details>
            <summary style={{ cursor: 'pointer', color: '#666', fontSize: 13 }}>
              Detalles técnicos (para reportar el bug)
            </summary>
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: 'grey.900',
                color: 'grey.100',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: 11,
                whiteSpace: 'pre-wrap',
                maxHeight: 400,
                overflow: 'auto',
              }}
            >
              <strong>Error:</strong> {error.stack || String(error)}
              {info?.componentStack && (
                <>
                  {'\n\n'}
                  <strong>Component stack:</strong>
                  {info.componentStack}
                </>
              )}
            </Box>
          </details>
        </Paper>
      </Box>
    );
  }
}